import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faSpinner, faExclamationTriangle, faRefresh } from '@fortawesome/free-solid-svg-icons';
import styled, { css } from '@xstyled/styled-components';
import { callFromAppTriggerEndPoint } from '../../common/Constants';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { useMsal } from '@azure/msal-react';

// #region Styles
const ResponseContainer = styled.div`
    background-color: #eee;
    padding: clamp(0px, 1vw, 5px);
    border-radius: clamp(1px, 6px, 10px);
    border: 1px solid #ccc;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: clamp(2px, 4px, 10px);
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.6s ease, transform 0.6s ease;
    width: clamp(93%, 96%, 99%);
`;

const TemporaryHeader = styled.div`
    display: flex;
    align-items: center;
    opacity: 1;
    transition: opacity 0.6s ease;
    font-size: clamp(10px, 2vw, 18px);
`;
const ErrorHeader = styled.div`
    display: flex;
    align-items: center;
    opacity: 1;
    transition: opacity 0.6s ease;
    font-size: clamp(10px, 2vw, 18px);

    svg {
        margin-right: 10px;
    }
`;

const ResponseHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: clamp(1px, 1vw, 5px) clamp(2px, 1vw, 10px);
    border-radius: clamp(1px, 1vw, 5px);
    cursor: pointer;

    h4 {
        margin: 0;
        font-size: clamp(10px, 2vw, 18px);
        align-items: flex-start;
    }
    span {
        font-size: clamp(8px, 2vw, 14px);
        font-weight: bold;
        display: flex;
        align-items: center;
    }
`;

const ResponseContent = styled.div.withConfig({ shouldForwardProp: (prop) => prop !== 'isCollapsed' })`
    margin-top: 2px;
    padding: 1px clamp(2px, 1vw, 10px);
    background-color: #f5f5f5;
    border-radius: clamp(0px, 1vw, 5px);
    margin-bottom: clamp(2px, 1vw, 10px);
    transition: opacity 0.6s ease, transform 0.6s ease;
    width: 98%;
    font-size: clamp(10px, 2vw, 16px);

  ${({ isCollapsed }) =>
        isCollapsed &&
        css`
            display: none;
    `}
`;

const DetailsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: #fdc8fb55;
    padding: clamp(1px, 2px, 5px) clamp(2px, 5px, 10px);
    border-radius: clamp(0px, 1px, 5px);
    cursor: pointer;

    h4 {
        margin: 0;
    font-size: clamp(0.8em, 1vw, .95em);
        align-items: flex-start;
    }
    span {
        font-size: clamp(9px, 1vw, 12px);
        font-weight: bold;
        display: flex;
        align-items: center;
    }
`;

const DetailsContent = styled.div.withConfig({ shouldForwardProp: (prop) => prop !== 'isCollapsed' })`
    margin-top: 2px;
    padding: clamp(1px, 2px, 3px) clamp(2px, 4px, 10px) 1px clamp(2px, 4px, 10px);
    background-color: #fdc8fb55;
    border-radius: clamp(0px, 2px, 5px);
    font-size: clamp(0.75em, 1vw, 0.9em);
    color: #000000;
    font-weight: lighter;
    transition: opacity 0.6s ease, transform 0.6s ease;

    ${({ isCollapsed }) =>
        isCollapsed &&
        css`
            display: none;
        `
    }
`;

// #endregion Styles

const AICharacter = ({
    responseID,
    characterConfig,
    userInput,
    previousResponses,
    isFakeResponse,
    isSummarizer,
    isResponseOpen,
    toggleResponse,
    onResponseFetched,
    onCheckboxChange,
    isChecked,
    onDeleteResponse
}) => {
    // #region AICharacter Code
    // #region State
    const [response, setResponse] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasFetched, setHasFetched] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [updatedUserMessage, setUpdatedUserMessage] = useState('');
    const [shouldShowDetails] = useState(true); // so we can turn this off in a config eventually
    const [isError, setIsError] = useState(false); // State to handle error display
    const [errorThatOccured, setErrorThatOccured] = useState(''); // State to store the error that occured

    const { instance, accounts } = useMsal();
    const memoisedHasFetched = useMemo(() => hasFetched, [hasFetched]);
    // #endregion State
    // #region Hooks    


    const aquireResponse = useCallback(async () => {
        let combinedUserMessage = '';
        if (isSummarizer) {
            combinedUserMessage = previousResponses
                ? `${userInput} Previous advice: "${previousResponses}"`
                : `${userInput} I have not received any previous advice.`;
        } else {
            const consultantPrompt = ``;
            combinedUserMessage = previousResponses
                ? `${consultantPrompt}Situation: ${userInput}. I have received the following previous advice: "${previousResponses}"`
                : `${consultantPrompt}Situation: ${userInput}. I have not received any previous advice.`;
        }

        setUpdatedUserMessage(combinedUserMessage);
        setIsLoading(true);
        setIsError(false);

        instance.acquireTokenSilent({
            scopes: ["https://aiconsultantllc.onmicrosoft.com/4f2dc587-5caa-47a1-860d-6d4049611d78/make_openai_call"],
            account: accounts[0],
        }).then(async (tokenResponse) => {

            try {

                const payload = {
                    "messages": [
                        {
                            "role": 'system',
                            "content": characterConfig.systemMessage
                        },
                        {
                            "role": "user",
                            "content": combinedUserMessage
                        },
                        {
                            "role":
                                "assistant",
                            "content": characterConfig.assistantMessage
                        }
                    ],
                    "temperature": characterConfig.temperature,
                    "top_p": characterConfig.top_p,
                    "max_tokens": characterConfig.max_tokens
                };
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokenResponse.accessToken}`
                };
                ////console.debug('Endpoint: ', callFromAppTriggerEndPoint);
                ////console.debug('Payload: ', JSON.stringify(payload), 'Date: ', formattedDate);

                try {
                    const callResponse = await axios.post(callFromAppTriggerEndPoint, JSON.stringify(payload), { headers });
                    ////console.debug('Response:', callResponse);
                    const content = callResponse.data.choices?.[0]?.message?.content || 'Something bad happened during the axios.post to the OpenAI endpoint.';

                    setResponse(content);
                    if (onResponseFetched) onResponseFetched(content);
                    setIsLoading(false);
                    setHasFetched(true);
                } catch (error) {
                    //console.error('Error occurred:', JSON.stringify(error));
                    setIsError(true); // Set error state to true
                    setIsLoading(false);

                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        //console.error('Response status:', error.response.status);
                        //console.error('Response data:', error.response.data);
                    } else if (error.request) {
                        // The request was made but no response was received
                        //console.error('No response received:', error.request);
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        //console.error('Error message:', error.message);
                    }
                    //console.error('Config:', error.config);
                    setErrorThatOccured(error.message);
                }

            } catch (error) {
                //console.error('AICharacter.js - Failed to fetch AI response:', JSON.stringify(error));
                const errorResponse = 'Error: Failed to fetch AI response.';
                setResponse(errorResponse);
                setIsError(true); // Set error state to true
                if (onResponseFetched) onResponseFetched(errorResponse);
                setIsLoading(false);
                setHasFetched(true);
                setErrorThatOccured(error.message);
            }
        }).catch((error) => {
            //console.error('Error acquiring token:', error);
            setErrorThatOccured(error.message);
        });
    }, [instance, accounts, characterConfig, userInput, previousResponses, onResponseFetched, isSummarizer]);

    useEffect(() => {
        if (!memoisedHasFetched) {
            const fetchResponse = async () => {
                if (isFakeResponse) {
                    setTimeout(() => {
                        const fakeResponse = 'Sure, I will consult with four specialized AI consultants to get the best advice for your situation...';
                        setResponse(fakeResponse);
                        if (onResponseFetched) onResponseFetched(fakeResponse);
                        setIsLoading(false);
                        setHasFetched(true);
                    }, 200);
                } else {
                    await aquireResponse();
                };
            };
            fetchResponse();
        }
    }, [memoisedHasFetched, characterConfig, userInput, previousResponses, isFakeResponse, onResponseFetched, isSummarizer, aquireResponse]);

    // #endregion Hooks
    // #region Handlers
    const toggleDetails = () => {
        setIsDetailsOpen(!isDetailsOpen);
        ////console.debug('Details toggled ', isDetailsOpen);
    };

    const handleCheckBoxClick = (e) => {
        e.stopPropagation();
        onCheckboxChange(responseID);
    }
    const handleRetryClick = () => {
        setIsError(false);
        setHasFetched(false);
    }

    // #endregion Handlers
    // #endregion AICharacter Code
    return (
        <ResponseContainer>
            {isLoading && !isError && (
                <TemporaryHeader>
                    <FontAwesomeIcon icon={faSpinner} spin={isLoading} />
                    {characterConfig.name} is responding...
                </TemporaryHeader>
            )}
            {isError && (
                <ErrorHeader>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <div>Error: Failed to fetch AI response. {errorThatOccured}</div>
                    <FontAwesomeIcon icon={faRefresh} onClick={handleRetryClick} />
                </ErrorHeader>
            )}
            {!isLoading && !isError && (
                <>
                    <ResponseHeader data-testid='response-header' onClick={toggleResponse}>
                        <h4>{characterConfig.name} responded:</h4>
                        <span>
                            <FontAwesomeIcon
                                data-testid='delete-button'
                                icon={faTrash}
                                onClick={() => onDeleteResponse(responseID)}
                            />

                            <input
                                data-testid='include-response-checkbox'
                                type="checkbox"
                                onChange={handleCheckBoxClick}
                                checked={isChecked}
                                onClick={(e) => e.stopPropagation()}
                                title="Check to include this response in the summarization."
                            />
                            <FontAwesomeIcon icon={isResponseOpen ? faChevronDown : faChevronRight} />
                        </span>
                    </ResponseHeader>
                    <ResponseContent data-testid='response-content' isCollapsed={!isResponseOpen}>
                        <ReactMarkdown>{response}</ReactMarkdown>
                        {shouldShowDetails && (
                            <>
                                <DetailsHeader onClick={toggleDetails}>
                                    <h4>Details</h4>
                                    <span>
                                        <FontAwesomeIcon icon={isDetailsOpen ? faChevronDown : faChevronRight} />
                                    </span>
                                </DetailsHeader>
                                <DetailsContent isCollapsed={!isDetailsOpen}>
                                    <p><strong>User Input:</strong>{`"${userInput}"`}</p>
                                    <p><strong>Previous Input:</strong> {`"${previousResponses}"`}</p>
                                    <p><strong>System Message:</strong> {characterConfig.systemMessage}</p>
                                    <p><strong>Assistant Message:</strong> {characterConfig.assistantMessage}</p>
                                    <p><strong>Combined Input:</strong> {updatedUserMessage}</p>
                                </DetailsContent>
                            </>
                        )}
                    </ResponseContent>
                </>

            )}
        </ResponseContainer>
    );
};

export default AICharacter;
